import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { 
  calculateHoursSummary, 
  calculateBillableValue,
  type HoursSummary 
} from '@/lib/timer-calculations';

export interface DailySummary extends HoursSummary {
  billableValue: number;
  entryCount: number;
}

/**
 * Hook para calcular resumo diário de horas
 * Usa a data fornecida no formato YYYY-MM-DD
 */
export function useDailySummary(date: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['daily-summary', date, user?.id],
    queryFn: async (): Promise<DailySummary> => {
      if (!user?.id) {
        return {
          totalMinutes: 0,
          totalHours: 0,
          billableMinutes: 0,
          billableHours: 0,
          nonBillableMinutes: 0,
          nonBillableHours: 0,
          billableValue: 0,
          entryCount: 0,
        };
      }

      // Interpret YYYY-MM-DD as local day boundaries
      // Usa início do próximo dia com .lt() para cobertura completa
      const startOfDayLocal = new Date(`${date}T00:00:00`);
      const nextDayLocal = new Date(`${date}T00:00:00`);
      nextDayLocal.setDate(nextDayLocal.getDate() + 1);

      const { data, error } = await supabase
        .from('time_entries')
        .select('duration_minutes, is_billable, hourly_rate')
        .eq('user_id', user.id)
        .gte('start_time', startOfDayLocal.toISOString())
        .lt('start_time', nextDayLocal.toISOString());

      if (error) throw error;

      const entries = data || [];
      const summary = calculateHoursSummary(entries);
      const billableValue = calculateBillableValue(entries);

      return {
        ...summary,
        billableValue,
        entryCount: entries.length,
      };
    },
    enabled: !!user?.id && !!date,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook para calcular resumo mensal de horas
 */
export function useMonthlySummary() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['monthly-summary', user?.id],
    queryFn: async (): Promise<DailySummary> => {
      if (!user?.id) {
        return {
          totalMinutes: 0,
          totalHours: 0,
          billableMinutes: 0,
          billableHours: 0,
          nonBillableMinutes: 0,
          nonBillableHours: 0,
          billableValue: 0,
          entryCount: 0,
        };
      }

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      // Primeiro dia do próximo mês com .lt() para cobertura completa
      const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);

      const { data, error } = await supabase
        .from('time_entries')
        .select('duration_minutes, is_billable, hourly_rate')
        .eq('user_id', user.id)
        .gte('start_time', startOfMonth.toISOString())
        .lt('start_time', startOfNextMonth.toISOString());

      if (error) throw error;

      const entries = data || [];
      const summary = calculateHoursSummary(entries);
      const billableValue = calculateBillableValue(entries);

      return {
        ...summary,
        billableValue,
        entryCount: entries.length,
      };
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
  });
}
